import React, { useState } from 'react';
import { tokens } from '../tokens';

// Professional Card component replacing scattered card styles
const Card = React.forwardRef(({
  children,
  variant = 'default',
  hover = false,
  padding = 'md',
  className = '',
  style: styleProp = {},
  ...props
}, ref) => {
  const [isHovered, setIsHovered] = useState(false);

  // Base card styles
  const baseStyles = {
    backgroundColor: tokens.colors.dark.card,
    borderRadius: tokens.borderRadius.xl,
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: tokens.colors.dark.border,
    overflow: 'hidden',
    transition: `all ${tokens.transitionDuration.normal} ${tokens.transitionTimingFunction.ease}`,
  };

  // Padding variants
  const paddingStyles = {
    none: { padding: 0 },
    sm: { padding: tokens.spacing[4] },
    md: { padding: tokens.spacing[6] },
    lg: { padding: tokens.spacing[8] },
  };

  // Card variants
  const variants = {
    default: {
      boxShadow: tokens.boxShadow.base,
    },
    elevated: {
      boxShadow: tokens.boxShadow.xl,
      borderTopWidth: '2px',
      borderTopStyle: 'solid',
      borderTopColor: tokens.colors.primary[400],
    },
    outlined: {
      boxShadow: 'none',
      borderColor: tokens.colors.dark.border,
      borderStyle: 'dashed',
    },
    gradient: {
      background: `linear-gradient(135deg, ${tokens.colors.dark.card} 0%, ${tokens.colors.dark.surface} 100%)`,
      boxShadow: tokens.boxShadow.sm,
    },
  };

  // Hover effects applied via state
  const hoverStyles = (hover && isHovered) ? {
    transform: 'translateY(-2px)',
    boxShadow: tokens.boxShadow.lg,
    borderColor: `${tokens.colors.primary[400]}60`,
  } : {};

  const cardStyles = {
    ...baseStyles,
    ...paddingStyles[padding],
    ...variants[variant],
    ...hoverStyles,
    ...styleProp,
  };

  return (
    <div
      ref={ref}
      style={cardStyles}
      className={className}
      onMouseEnter={hover ? () => setIsHovered(true) : undefined}
      onMouseLeave={hover ? () => setIsHovered(false) : undefined}
      {...props}
    >
      {children}
    </div>
  );
});

Card.displayName = 'Card';

// Card Header component
export const CardHeader = ({ children, className = '', ...props }) => (
  <div
    style={{
      paddingBottom: tokens.spacing[4],
      marginBottom: tokens.spacing[4],
      borderBottom: `1px dashed ${tokens.colors.dark.border}`,
    }}
    className={className}
    {...props}
  >
    {children}
  </div>
);

// Card Content component  
export const CardContent = ({ children, className = '', ...props }) => (
  <div className={className} {...props}>
    {children}
  </div>
);

// Card Footer component
export const CardFooter = ({ children, className = '', ...props }) => (
  <div
    style={{
      paddingTop: tokens.spacing[4],
      marginTop: tokens.spacing[4],
      borderTop: `1px dashed ${tokens.colors.dark.border}`,
    }}
    className={className}
    {...props}
  >
    {children}
  </div>
);

export default Card;